import React, { useEffect, useId, useRef, useState } from 'react';
import { ChevronDown, Search, X } from 'lucide-react';
import clsx from 'clsx';
import type { BlankFilter, TextFilter as TextFilterValue } from '@app-types';
import { useDismissable } from '@hooks/useDismissable';
import field from './filterField.module.css';
import styles from './TextFilter.module.css';

export interface TextFilterProps {
  columnLabel: string;
  value?: TextFilterValue | BlankFilter;
  onChange: (value: TextFilterValue | BlankFilter | null) => void;
  /** Offer "no value" too, under this label; absent on a column that can never be empty. */
  emptyFilterLabel?: string;
  /** Cap on what can be typed or pasted; absent on a page whose API sets no limit. */
  maxLength?: number;
}

/**
 * Free-text filter for a column, with an optional "no value" choice (ADO-1118).
 *
 * The choice lives behind the trailing chevron rather than as a magic word typed
 * into the box: typing "Unassigned" is, and stays, a search for a person whose
 * name contains it. Once chosen, the field shows the label in place of the input,
 * because a text query and "no value at all" cannot both hold.
 */
const TextFilter: React.FC<TextFilterProps> = ({
  columnLabel,
  value,
  onChange,
  emptyFilterLabel,
  maxLength,
}) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const clearBlankRef = useRef<HTMLButtonElement>(null);
  const menuItemRef = useRef<HTMLButtonElement>(null);
  const menuId = useId();
  const isBlank = value?.kind === 'blank';

  // Choosing or clearing "no value" swaps the input for the label and back. The
  // control that had focus is gone after either swap, so move it to the one that
  // replaced it — but only after a swap this control made, never on first render.
  const swappedByUser = useRef(false);
  useEffect(() => {
    if (!swappedByUser.current) return;
    swappedByUser.current = false;
    (isBlank ? clearBlankRef : inputRef).current?.focus();
  }, [isBlank]);

  useEffect(() => {
    if (isMenuOpen) menuItemRef.current?.focus();
  }, [isMenuOpen]);

  useDismissable(containerRef, isMenuOpen, () => setIsMenuOpen(false));

  if (isBlank) {
    return (
      <div className={clsx(field.field, field.hasValue)}>
        <span className={field.summary}>{emptyFilterLabel ?? 'Blank'}</span>
        <button
          ref={clearBlankRef}
          type="button"
          className={field.affordance}
          onClick={() => {
            swappedByUser.current = true;
            onChange(null);
          }}
          aria-label={`Clear ${columnLabel} filter`}
        >
          <X size={14} />
        </button>
      </div>
    );
  }

  const textValue = value?.value ?? '';

  return (
    <div className={styles.textFilter} ref={containerRef}>
      <div className={clsx(field.field, textValue && [field.hasValue, styles.hasValue])}>
        <Search size={14} className={styles.icon} aria-hidden="true" />
        <input
          ref={inputRef}
          type="text"
          className={styles.input}
          placeholder="Filter..."
          value={textValue}
          maxLength={maxLength}
          onChange={(e) => onChange(e.target.value ? { kind: 'text', value: e.target.value } : null)}
          aria-label={`Filter by ${columnLabel}`}
        />
        {/* One trailing slot, the same 18px every control in this row reserves:
            the Clear takes it once there is something to clear, otherwise the
            chevron for the no-value choice, if the column has one. */}
        {textValue ? (
          <button
            type="button"
            className={field.affordance}
            onClick={() => onChange(null)}
            aria-label={`Clear ${columnLabel} filter`}
          >
            <X size={14} />
          </button>
        ) : (
          emptyFilterLabel && (
            <button
              type="button"
              className={clsx(field.affordance, styles.menuTrigger)}
              onClick={() => setIsMenuOpen((open) => !open)}
              aria-expanded={isMenuOpen}
              aria-controls={menuId}
              aria-label={`More ${columnLabel} filters`}
            >
              <ChevronDown size={14} />
            </button>
          )
        )}
      </div>

      {/* A disclosure holding one button, not an ARIA menu: a single choice needs
          no arrow-key roving, and Tab, Enter and Escape already do the right thing. */}
      {isMenuOpen && emptyFilterLabel && (
        <div id={menuId} className={clsx(field.panel, styles.menu)}>
          <button
            ref={menuItemRef}
            type="button"
            className={styles.menuItem}
            onClick={() => {
              setIsMenuOpen(false);
              swappedByUser.current = true;
              onChange({ kind: 'blank' });
            }}
          >
            {emptyFilterLabel}
          </button>
        </div>
      )}
    </div>
  );
};

export default TextFilter;
